// @flow

/*
 *  Copyright 2017 Adobe Systems Incorporated. All rights reserved.
 *  This file is licensed to you under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License. You may obtain a copy
 *  of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software distributed under
 *  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 *  OF ANY KIND, either express or implied. See the License for the specific language
 *  governing permissions and limitations under the License.
 *
 */

import KaizenScss from './Kaizen.scss';
import AppsUtil from '../../../../utils/AppsUtil';
import CompatibilityUtil from '../../../../utils/CompatibilityUtil';
import DeviceDetectionUtil from '../../../../utils/DeviceDetectionUtil';
import CCDUtil from '../../../../utils/CCDUtil';
import {analytics, additionalDownloadMessageData} from '../../../../constants/ccdConstants';
import DownloadCoachMark from '../../../../components/DownloadCoachMark/DownloadCoachMark';
import MessagingBox from '../../Messaging/MessagingBox/MessagingBox';
import React, {Component} from 'react';
import CCDIngestAnalytics from '../../../../analytics/CCDIngestAnalytics';
import ccdDtmAnalytics from '../../../../analytics/CCDDTMAnalytics';
import getConfigApps from '../../../../config';
import {getLocalizedURL} from '@intl/adobe-locales';
import Url from '../../../../utils/Url';
import {downloadingData, getContextGuid, clickData, prevVersionData, sendIngestLoggedOutPromotionExpEvent} from '../../../../utils/CCDAnalyticsUtils';
import DownloadUtil from '../../../../utils/DownloadUtil';

type Props = {
	genericAnalyticEvent?: Function,
	product?: Object,
	routeParams: Object,
	intl: Object,
	sendAnalytic?: Function,
	environment: string,
	isCCH?: boolean,
	locale: string,
	loadHomePage?: Function,
	sendAnalyticCC?: Function,
	genericPDPAnalyticEvent?: ?Function,
	loadHomePage?: Function,
	imsData: Object
};


export default class Kaizen extends Component<Props> {
	productDetails: Object;
	imageUrls: Object;
	genericPDPAnalyticEvent: ?Function

	constructor(props: Props) {
		super(props);
		this.productDetails = this.props.isCCH && this.props.product ? this.props.product : CCDUtil.getProductsData(this.props.routeParams.productId);
		this.imageUrls = this.setImageUrls();
		this.genericPDPAnalyticEvent = this.props.genericPDPAnalyticEvent;
	}

	sendAnalytic(type: string) {
		let isOlderVersion = false;
		if (this.productDetails.sapCode !== 'KCCC') {
			isOlderVersion = !CompatibilityUtil.isAppSupported(this.productDetails.sapCode) && CompatibilityUtil.isAnyOlderVersionSupported(this.productDetails.sapCode);
		}
		const ccdIngestAnalytics = new CCDIngestAnalytics(this.props.environment);
		ccdIngestAnalytics.initAnalytics();
		let outcome = true;
		if (this.productDetails.id === 'lightroom-classic' && Url.getParam('photoplanRedirect')) {
			outcome = 'false-lr-cc/true-older-lr-classic';
		}

		let data = {};
		switch (type) {
			case 'downloadStart':
				if (this.props.isCCH) {
					return;
				}
				data = isOlderVersion ? CCDUtil.getCloneObject(prevVersionData) : CCDUtil.getCloneObject(downloadingData);
				break;
			case 'restartDownloadClicked' :
				data = CCDUtil.getCloneObject(clickData);
				data.subType = 'restart-download';
				break;
			case 'needHelpClicked' :
				data = CCDUtil.getCloneObject(clickData);
				data.subType = 'help';
				break;
			case 'returnAdobeComClicked' :
				data = CCDUtil.getCloneObject(clickData);
				data.subType = 'adobe.com';
				break;
		}
		data.componentName = 'Kaizen';
		data.productId = this.productDetails.id;
		data.contextGuid = getContextGuid(isOlderVersion, false, outcome);
		if (this.props.isCCH && type !== 'returnAdobeComClicked') {
			data.workflow = 'Apps';
			data.productName = this.productDetails.name;
		}
		ccdIngestAnalytics.genericAnalyticEvent(data);
	}

	// Settoing proper image paths.
	setImageUrls() {
		return {
			installImageOne: CCDUtil.getUrlPath('/assets/ccd/images/install_rebrand/kaizen_download.svg'),
			installImageTwo: CCDUtil.getUrlPath('/assets/ccd/images/install_rebrand/kaizen_download.svg'),
			installImageTwoSafari: CCDUtil.getUrlPath('/assets/ccd/images/install_rebrand/kaizen_download_safari.svg')
		};
	}

	// Adding Blue messagings for win10, older version supported, and muse.
	renderToastMessaging() {
		if (this.productDetails.id === 'muse') {
			return <MessagingBox isCCH={this.props.isCCH} key={`boxMessaging${this.productDetails.id}`} hasThor={false} environment={this.props.environment} productDetails={this.productDetails}
				messageCode='customMessageMuse' locale={this.props.locale} formatMessage={this.props.intl.formatMessage} genericPDPAnalyticEvent={this.genericPDPAnalyticEvent}/>;
		}
		if (AppsUtil.isWin10() && this.productDetails.sapCode !== 'KCCC') {
			return <MessagingBox isCCH={this.props.isCCH} environment={this.props.environment} productDetails={this.productDetails}
				messageCode='win10Message' locale={this.props.locale} formatMessage={this.props.intl.formatMessage} genericPDPAnalyticEvent={this.genericPDPAnalyticEvent}/>;
		}
		if (this.productDetails.sapCode !== 'KCCC' && !CompatibilityUtil.isAppSupported(this.productDetails.sapCode) && CompatibilityUtil.isAnyOlderVersionSupported(this.productDetails.sapCode)) {
			return <MessagingBox isCCH={this.props.isCCH} hasThor={false} environment={this.props.environment} productDetails={this.productDetails}
				messageCode='olderVersionMessage' locale={this.props.locale} formatMessage={this.props.intl.formatMessage} genericPDPAnalyticEvent={this.genericPDPAnalyticEvent}/>;
		}
		return null;
	}

	// Starting download and showing Download Coachmark.
	startDownload() {
		const downloadCoachMark = <DownloadCoachMark isNewDesign={true} intl={this.props.intl}/>;
		if (!this.props.isCCH && this.props.routeParams.productId === 'creative-cloud' && this.props.routeParams.pageType === 'starter') {
			DownloadUtil.downloadProduct({sapcode:'PHSP',hasThor:false, isAllApps:true, downloadCoachMark});
		}
		else if (this.productDetails.sapCode === 'KCCC') {
			DownloadUtil.downloadProduct({sapcode:'KCCC', hasThor:false, checkCompatability:false ,isAllApps:true, downloadCoachMark});
		}
		else {
			DownloadUtil.downloadProduct({sapcode:this.productDetails.sapCode,hasThor:false, downloadCoachMark});
		}
		if (!this.props.isCCH) {
			ccdDtmAnalytics.reportDTMEvent(`DownloadCenter:DownloadClick:InstallAAM:${this.props.routeParams.productId}`, CCDUtil.getDTMEventData(), 'state', this.props.imsData.isSignedUser);
		}
	}

	getGoURL(base: string) {
		return AppsUtil.getCCDGoURL(this.props.locale, base);
	}

	componentDidMount() {
		if (!this.props.isCCH) {
			ccdDtmAnalytics.reportDTMEvent(`DownloadCenter:Download:PageView:${this.props.routeParams.productId}`, CCDUtil.getDTMEventData(), 'state', this.props.imsData.isSignedUser);
		}
		this.startDownload();
		if (!this.props.isCCH && AppsUtil.isLoggedOutPromotionExp) {
			// Send legacy Ingest Kaizen download event for promotion workflow
			sendIngestLoggedOutPromotionExpEvent(this.props.routeParams.productId, 'ccpd-dl-start');
		}
		this.sendAnalytic(analytics.downloadStart);
		this.setDescriptionHeight();
	}

	setDescriptionHeight() {
		const installText = document.querySelectorAll(`.${KaizenScss.kaizenStepText}`);
		if (installText && installText.length === 2) {
			if (installText[0].offsetHeight > installText[1].offsetHeight) {
				installText[1].style.height = `${installText[0].offsetHeight}px`;
			}
			else {
				installText[0].style.height = `${installText[1].offsetHeight}px`;
			}
		}
	}

	getInstallStepTwoText() {
		const appName = this.productDetails.name;
		const secondaryAppName = additionalDownloadMessageData[this.productDetails.sapCode] && additionalDownloadMessageData[this.productDetails.sapCode].additionalProductsList[0] ? CCDUtil.getProductsData(additionalDownloadMessageData[this.productDetails.sapCode].additionalProductsList[0]).name : undefined;
		const tertiaryAppName = additionalDownloadMessageData[this.productDetails.sapCode] && additionalDownloadMessageData[this.productDetails.sapCode].additionalProductsList[1] ? CCDUtil.getProductsData(additionalDownloadMessageData[this.productDetails.sapCode].additionalProductsList[1]).name : undefined;
		let browser = DeviceDetectionUtil.getBrowser();
		if (browser === null) {
			browser = 'chrome';
		}
		if (this.productDetails.id === 'creative-cloud') {
			return `${this.props.intl.formatMessage({id: `ccd.kaizen.installStepTwo.prompt.${browser}`})}`;
		}
		if (additionalDownloadMessageData[this.productDetails.sapCode]) {
			if (browser === 'safari') {
				return `${this.props.intl.formatMessage({id: 'ccd.kaizen.installStepTwo.safari.additionalApps'}, {appName})} ${this.props.intl.formatMessage({id: additionalDownloadMessageData[this.productDetails.sapCode].kaizenMessageId}, {
					appName,
					secondaryAppName,
					tertiaryAppName,
					b: str => <b>{str}</b>
				})}`;
			}
			return `${this.props.intl.formatMessage({id: `ccd.kaizen.installStepTwo.prompt.${browser}`})} ${this.props.intl.formatMessage({id: additionalDownloadMessageData[this.productDetails.sapCode].kaizenMessageId}, {
				appName,
				secondaryAppName,
				tertiaryAppName,
				b: str => <b>{str}</b>
			})}`;
		}
		return `${this.props.intl.formatMessage({id: `ccd.kaizen.installStepTwo.prompt.${browser}`}, {appName})} ${this.productDetails.sapCode !== 'KCCC' ? this.props.intl.formatMessage({id: 'ccd.kaizen.installStepTwo.product'}) : ''}`;
	}

	// Installation steps.
	renderKaizenInstallSection() {
		const fileName = DownloadUtil.getDownloadFileName(this.productDetails.sapCode);
		const altTextPrefix = `${this.props.intl.formatMessage({id: 'apps.tile.install'})} ${this.productDetails.name}`;

		if (DeviceDetectionUtil.isSafari()) {
			return (
				<div className={KaizenScss.installSection}>
					<div className={KaizenScss.kaizenStep}>
						<div className={KaizenScss.kaizenStepText}>
							{`1. ${this.props.intl.formatMessage({id: 'ccd.kaizen.installStepOne'}, {fileName})}`}
						</div>
						<img alt={`${altTextPrefix} ${this.props.intl.formatMessage({id: 'apps.label.step1'})}`} className={KaizenScss.kaizenStepImgSafari1} src={this.imageUrls.installImageOne}/>
					</div>
					<div className={KaizenScss.kaizenStep}>
						<div className={KaizenScss.kaizenStepText}>
							{`2. ${this.getInstallStepTwoText()}`}
						</div>
						<img alt={`${altTextPrefix} ${this.props.intl.formatMessage({id: 'apps.label.step2'})}`} className={`${KaizenScss.kaizenStepImgSafari2} ${KaizenScss.ImgSafari2Rebrand}`} src={this.imageUrls.installImageTwoSafari}/>
					</div>
				</div>
			);
		}
		return (
			<div className={KaizenScss.installSection}>
				<div className={KaizenScss.kaizenStep}>
					<div className={KaizenScss.kaizenStepText}>
						{this.getInstallStepTwoText()}
					</div>
					<img alt={`${altTextPrefix} ${this.props.intl.formatMessage({id: 'apps.label.step2'})}`} className={KaizenScss.kaizenStepImg} src={this.imageUrls.installImageTwo}/>
				</div>
			</div>
		);
	}

	getTitle(appName: string) {
		if (DeviceDetectionUtil.isSafari()) {
			return this.props.intl.formatMessage({id: 'ccd.kaizen.title1'}, {appName});
		}
		return this.props.intl.formatMessage({id: 'ccd.kaizen.title2'},{appName});
	}

	navigate(url: string) {
		window.location = url;
	}

	onRestartDownload(e: Object) {
		e.preventDefault();
		if (this.props.isCCH) {
			if (this.productDetails.sapCode === 'KCCC') {
				DownloadUtil.downloadProduct({sapcode:'KCCC', hasThor:false, checkCompatability:false ,isAllApps:true});
			}
			else {
				DownloadUtil.downloadProduct({sapcode:this.productDetails.sapCode,hasThor:false});
			}
		}
		else {
			this.startDownload();
			// Send legacy Ingest Kaizen retry download event for promotion workflow
			if (AppsUtil.isLoggedOutPromotionExp) {
				sendIngestLoggedOutPromotionExpEvent(this.props.routeParams.productId, 'ccpd-dl-start', 'ccpd-dl-through-try');
			}
		}
		if (this.productDetails.sapCode === 'KCCC' && this.props.isCCH && this.props.sendAnalyticCC) {
			this.props.sendAnalyticCC('restart-download');
		}
		else {
			this.sendAnalytic(analytics.restartDownloadClicked);
			this.props.isCCH && this.props._onAppDownloadEvent(true);
		}
	}

	onHelpClick() {
		if (this.productDetails.sapCode === 'KCCC' && this.props.isCCH && this.props.sendAnalyticCC) {
			this.props.sendAnalyticCC('need-help');
		}
		else {
			this.sendAnalytic(analytics.needHelpClicked);
		}
	}

	render() {
		const appName = this.productDetails.name;
		return (
			<div className={`${KaizenScss.installationDialog} ${DeviceDetectionUtil.isSafari() ? KaizenScss.safari : ''}`}>

				<div className={KaizenScss.title}>
					<span className={KaizenScss.productTitle}>
						{this.getTitle(appName)}
					</span>
				</div>

				{this.renderKaizenInstallSection()}

				<div className={KaizenScss.helpSection}>
					<span
						className={KaizenScss.tryAgainTitle}>{this.props.intl.formatMessage({id: 'ccd.kaizen.tryAgainTitle'})}</span>
					<a className={KaizenScss.tryAgainLink} href='#' target='download_frame'
						onClick={(e) => {this.onRestartDownload(e);}}>
						{this.props.intl.formatMessage({id: 'ccd.kaizen.tryAgainLink'})}
					</a>
					<span className={KaizenScss.tryAgainTitle}> | </span>
					<a className={KaizenScss.needHelpLink} onClick={() => {this.onHelpClick();}}
						href={getLocalizedURL('https://helpx.adobe.com/download-install.html?promoid=GHMVYGTH&mv=other', this.props.locale)} target='_blank'>
						{this.props.intl.formatMessage({id: 'ccd.kaizen.needHelpLink'})}
					</a>
					{!this.props.isCCH ? <a className={KaizenScss.goToAdobeSite} onClick={(e) => {e.preventDefault();this.sendAnalytic(analytics.returnAdobeComClicked);setTimeout(() => {this.navigate(getLocalizedURL(getConfigApps('servers').Adobe, this.props.locale));},1000);}}
						href={getConfigApps('servers').Adobe} >
						{this.props.intl.formatMessage({id: 'ccd.kaizen.returnToAdobeSite'})}
					</a> : null}
				</div>
				{this.renderToastMessaging()}
			</div>
		);
	}
}
